import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { append, map, path, pathOr, remove, update } from 'ramda'
import React from 'react'

import { Radio } from '@/app/component/atom/form'
import Conditions from '@/app/component/conditions'
import { Groups } from '@/app/component/conditions/definitions'
import ActionsBox from '@/app/module/campaigns/component/actions/actions-box'
import { GetImageProps, GetVoiceProps } from '@/app/module/campaigns/component/item/steps/content/part-config-props'
import { SMS_ACTION_DEFAULT_SENDERID } from '@/app/module/campaigns/definitions'
import { processConditions } from '@/app/module/campaigns/store/selectors'
import {
  CampaignTemplateType,
  ConditionActions,
  Language,
  LocalConditionActions,
  SenderIdOption,
} from '@/app/module/campaigns/types'
import { getActionOptionsForCampaign } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'
import { unwrapActions, wrapActions } from '@/app/module/campaigns/utils/actions/wrap'
import { CountryTimezone, FilesType, PersonalizationType, TimezoneWithCountryCodeType } from '@/app/types'

const DEFAULT_ATTEMPTS = 5
const MAX_ATTEMPTS_ON_PROGESS = 15
const MAX_ATTEMPTS = 5

type keyValue = {
  label: string
  value: any
}

type RadioValues = keyValue[]

const reconnectValues: RadioValues = [
  {
    label: 'Yes',
    value: 'true',
  },
  {
    label: 'No',
    value: 'false',
  },
]

const reconnectOnProgressValues: RadioValues = [
  {
    label: 'on survey progress',
    value: 'true',
  },
  {
    label: 'on calls longer than 10 seconds',
    value: 'false',
  },
]

const reconnectDefaultValues: RadioValues = [
  {
    label: `Use the default (${DEFAULT_ATTEMPTS})`,
    value: 'true',
  },
  {
    label: 'Let me decide',
    value: 'false',
  },
]

type Props = {
  actions: ConditionActions[]
  campaignType: CampaignTemplateType
  countriesTimezones: CountryTimezone[]
  countryCode: string
  defaultLanguage: string
  groups: Groups
  languages: Language[]
  orgId: number
  personalizationList: PersonalizationType
  reconnectMax: number
  reconnectOnProgress: boolean
  segments: Groups
  senderIds: SenderIdOption[]
  timezone: string
  timezones: TimezoneWithCountryCodeType
  token: string
  files: FilesType

  getImageProps: GetImageProps
  getVoiceProps: GetVoiceProps
  onActionsChange: (actions: ConditionActions[]) => void
  onConfigChange: (value: { reconnectMax: number; reconnectOnProgress: boolean }) => void
}

type ReconnectState = {
  reconnect: boolean
  reconnectOnProgress: boolean
  reconnectMax: number
  reconnectDefault: boolean
  reconnectValue: number
}

const reconnectInitialState: ReconnectState = {
  reconnect: true,
  reconnectOnProgress: true,
  reconnectMax: -1,
  reconnectDefault: true,
  reconnectValue: DEFAULT_ATTEMPTS,
}

type Action =
  | { type: 'SELECT_RECONNECT'; payload: boolean }
  | { type: 'SELECT_RECONNECT_ON_PROGESS'; payload: boolean }
  | { type: 'SELECT_RECONNECT_DEFAULT'; payload: boolean }
  | { type: 'SELECT_RECONNECT_VALUE'; payload: number }

type reducerFunc = (state: ReconnectState, action: Action) => ReconnectState

const reducer: reducerFunc = (state, action) => {
  switch (action.type) {
    case 'SELECT_RECONNECT':
      if (action.payload) {
        return {
          ...state,
          reconnect: true,
          reconnectMax: state.reconnectDefault ? -1 : state.reconnectValue,
        }
      }
      return {
        ...state,
        reconnect: false,
        reconnectMax: 0,
      }
    case 'SELECT_RECONNECT_ON_PROGESS':
      if (action.payload) {
        return {
          ...state,
          reconnectOnProgress: true,
        }
      }
      return {
        ...state,
        reconnectOnProgress: false,
        ...(state.reconnectMax > DEFAULT_ATTEMPTS && {
          reconnectMax: DEFAULT_ATTEMPTS,
          reconnectValue: DEFAULT_ATTEMPTS,
        }),
      }
    case 'SELECT_RECONNECT_DEFAULT':
      if (action.payload) {
        return {
          ...state,
          reconnectMax: -1,
          reconnectDefault: true,
        }
      }
      return {
        ...state,
        reconnectMax: DEFAULT_ATTEMPTS,
        reconnectValue: DEFAULT_ATTEMPTS,
        reconnectDefault: false,
      }
    case 'SELECT_RECONNECT_VALUE':
      return {
        ...state,
        reconnectMax: action.payload,
        reconnectValue: action.payload,
      }
    default:
      throw new Error('Unexpected reconnect action')
  }
}

const getInitialState = (reconnectMax: number, reconnectOnProgress: boolean) => {
  let initialState = { ...reconnectInitialState }
  if (reconnectOnProgress === false) {
    initialState.reconnectOnProgress = false
  }
  if (reconnectMax === 0) {
    initialState.reconnect = false
    initialState.reconnectMax = 0
  } else if (reconnectMax > 0) {
    initialState = {
      ...initialState,
      reconnectMax,
      reconnectDefault: false,
      reconnectValue: reconnectMax,
    }
  }
  return initialState
}

const Reconnect: React.FC<Props> = (props) => {
  const {
    getVoiceProps,
    getImageProps,
    senderIds,
    personalizationList,
    onActionsChange,
    orgId,
    token,
    campaignType,
    timezones,
    timezone,
    countriesTimezones,
    countryCode,
    onConfigChange,
    defaultLanguage,
    languages,
    files,
  } = props
  const [state, dispatch] = React.useReducer(reducer, getInitialState(props.reconnectMax, props.reconnectOnProgress))
  const [conditionsDisplay, setConditionsDisplay] = React.useState(-1)

  const actions = map(
    (action: ConditionActions) => ({
      ...action,
      actions: unwrapActions(action.actions),
    }),
    props.actions || [
      {
        when: null,
        actions: [],
      },
    ],
  )

  const stateReconnectMax = state.reconnectMax
  const stateReconnectOnProgress = state.reconnectOnProgress
  React.useEffect(() => {
    onConfigChange({
      reconnectMax: stateReconnectMax,
      reconnectOnProgress: stateReconnectOnProgress,
    })
  }, [stateReconnectMax, stateReconnectOnProgress, onConfigChange])

  const actionOptions = React.useMemo(
    () =>
      getActionOptionsForCampaign({
        campaignType,
        actionNamespace: ActionContext.Reconnect,
      }),
    [campaignType],
  )

  return (
    <Card style={{ marginBottom: '2rem' }} className="reconnect-cont">
      <CardHeader title="What if a call is disconnected during the survey?" style={{ padding: '12px 24px' }} />
      <CardContent>
        <div
          style={{
            marginBottom: '15px',
          }}
        >
          <Typography color="textSecondary" variant="body1">
            Sometimes a call drops while the survey is ongoing, for example because the phone has a bad signal. <br />
            Do you want us to call back the contact? We will continue the survey where they left off.
            <br />
            <strong>Note:</strong> We will not resume the survey if the survey decides to end the call, for example with
            a “hang up” action.
            <br />
            <ProtipText>
              <strong>Protip:</strong> Enabling this feature will very likely lead to a{' '}
              <strong>higher survey completion rate</strong>, and a lower overall{' '}
              <strong>cost per completed survey</strong>.
            </ProtipText>
          </Typography>
          <RadioContainer>
            <Radio
              id="reconnect-select"
              name="reconnect-select"
              label="Call back and resume the survey"
              value={String(state.reconnect)}
              values={reconnectValues}
              row={true}
              onChange={({ value }) => {
                dispatch({ type: 'SELECT_RECONNECT', payload: value === 'true' })
              }}
              error=""
            />
          </RadioContainer>
          {state.reconnect && (
            <div>
              <div>
                <FormControl variant="standard">
                  <Tooltip
                    title={
                      <>
                        We only call back if contacts indicate they wish to continue the survey. <br />
                        If you select “on survey progress”, then we will call back as long as the contact keeps moving
                        along with the survey, for example by answering questions. <br />
                        If you choose “on calls longer than 10 seconds”, we will call back only if the latest call was
                        longer than 10 seconds.
                      </>
                    }
                  >
                    <FormLabel>When to call back</FormLabel>
                  </Tooltip>
                  <Radio
                    id="reconnect-progress-select"
                    name="reconnect-progress-select"
                    value={String(state.reconnectOnProgress)}
                    values={reconnectOnProgressValues}
                    row={true}
                    onChange={({ value }: any) => {
                      dispatch({ type: 'SELECT_RECONNECT_ON_PROGESS', payload: value === 'true' })
                    }}
                    error=""
                  />
                </FormControl>
              </div>
              <div>
                <FormControl variant="standard">
                  <Tooltip
                    title={
                      <>
                        After how many times do you want to stop calling back? This is an additional safe guard: no
                        matter the other configuration, if you hit the maximum number of attempts, we will stop calling
                        back.
                        <br />
                        The default is {DEFAULT_ATTEMPTS}.
                      </>
                    }
                  >
                    <FormLabel>How often to call back at most?</FormLabel>
                  </Tooltip>
                  <Radio
                    id="reconnect-maximum-attempts-defaults"
                    name="reconnect-maximum-attempts-defaults"
                    value={String(state.reconnectDefault)}
                    values={reconnectDefaultValues}
                    row={true}
                    onChange={({ value }: any) => {
                      dispatch({ type: 'SELECT_RECONNECT_DEFAULT', payload: value === 'true' })
                    }}
                    error=""
                  />
                </FormControl>
              </div>
              {!state.reconnectDefault && (
                <FormControl variant="standard">
                  <Tooltip title="The highest number you can choose here depends on the other configuration. Reconnecting only on survey progress allows you to make more calls.">
                    <FormLabel>Define the maximum number of reconnect attempts</FormLabel>
                  </Tooltip>
                  <TextField
                    variant="standard"
                    id="reconnect-maximum-attempts-value"
                    error={state.reconnectValue < 1}
                    helperText={state.reconnectValue < 1 ? 'Maximum reconnect attempts cannot be less than 1' : ''}
                    value={`${state.reconnectValue}`}
                    inputProps={{
                      type: 'number',
                      min: 1,
                      max: state.reconnectOnProgress ? MAX_ATTEMPTS_ON_PROGESS : MAX_ATTEMPTS,
                    }}
                    onChange={(e) => {
                      dispatch({ type: 'SELECT_RECONNECT_VALUE', payload: Number(e.currentTarget.value) })
                    }}
                  />
                </FormControl>
              )}
            </div>
          )}
          {state.reconnect && (
            <div
              style={{
                marginTop: '15px',
              }}
            >
              <Typography variant="h6">What should we do when the call reconnects?</Typography>
              {actions.map((action: LocalConditionActions, index: number) => (
                <div
                  className="reconnect-action-set"
                  key={index}
                  style={{
                    marginTop: '15px',
                  }}
                >
                  {conditionsDisplay === index && (
                    <Conditions
                      groups={pathOr({}, ['groups'], props)}
                      segments={pathOr({}, ['segments'], props)}
                      data={processConditions(pathOr(['or', ['and', ['', '', '']]], ['when'], action))}
                      personalizationList={props.personalizationList}
                      title="Run these actions"
                      onSaveConditions={({ when }: any) => {
                        setConditionsDisplay(-1)
                        onActionsChange(
                          update(
                            index,
                            {
                              ...props.actions[index],
                              when,
                            },
                            props.actions,
                          ),
                        )
                      }}
                      onClose={() => setConditionsDisplay(-1)}
                    />
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="subtitle2" color="textSecondary">
                        Run these actions
                      </Typography>
                      <Button
                        color="primary"
                        onClick={() => setConditionsDisplay(index)}
                        size="small"
                        endIcon={<Icon style={{ fontSize: '13px' }}>launch</Icon>}
                      >{`${path(['when'], action) ? 'sometimes' : 'always'}`}</Button>
                    </div>
                    {index !== 0 && (
                      <div>
                        <IconButton
                          onClick={() => {
                            onActionsChange(remove(index, 1, props.actions))
                          }}
                        >
                          <Icon>clear</Icon>
                        </IconButton>
                      </div>
                    )}
                  </div>
                  <ActionsBox
                    actionOptions={actionOptions}
                    actions={action.actions || []}
                    countriesTimezones={countriesTimezones}
                    countryCode={countryCode}
                    defaultLanguage={defaultLanguage}
                    defaultSenderId={SMS_ACTION_DEFAULT_SENDERID.voiceQuestion}
                    disableLabel
                    files={files}
                    hasTranscribe={false}
                    hideFinalActions
                    index={index}
                    isCloseDisabled
                    languages={languages}
                    orgId={orgId}
                    personalizationList={personalizationList}
                    senderIds={senderIds}
                    timezone={timezone}
                    timezones={timezones}
                    token={token}
                    transcribeEnabled={false}
                    updateContactConfig={{ invalid: false, openResponse: false, question: false }}
                    voiceId={`reconnect-${index}`}
                    whatsappSenderIds={[]}
                    onChange={(newActions) => {
                      onActionsChange(
                        update(
                          index,
                          {
                            ...props.actions[index],
                            actions: wrapActions(newActions),
                          },
                          props.actions,
                        ),
                      )
                    }}
                    getImageProps={getImageProps}
                    getVoiceProps={getVoiceProps}
                  />
                  {index !== actions.length - 1 && <Divider />}
                </div>
              ))}
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  color="primary"
                  startIcon={<Icon>add</Icon>}
                  variant="contained"
                  onClick={() => {
                    onActionsChange(append({ when: null, actions: [] }, props.actions))
                  }}
                >
                  Add a set of actions
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

const ProtipText = styled('span')({
  color: '#2f80ac',
})

const RadioContainer = styled('div')({
  marginTop: '20px',
})

export default Reconnect
